<script lang="ts">
  /**
   * The projects page of the browser version: the projects stored in this
   * browser, importing and exporting them, and starting new ones. In Chrome
   * and Edge a project can also live in a folder on the user's computer,
   * edited in place.
   */
  import { onDestroy, onMount } from 'svelte';
  import { downloadZip } from './download.js';
  import type { ProjectDB, ProjectRecord } from './idb.js';
  import { canOpenFolders, folderAccess, FolderStorage, isEmptyFolder, pickFolder, requestFolderAccess, walk, writeFiles } from './folder.js';
  import { emptyProject, filesFromFolder, filesFromZip, projectFiles, summarize, withGuide, withName, type ProjectSummary } from './projects.js';
  import { exampleProject } from './starter.js';
  import { FIRST_PARTY_PLUGINS, guideFiles } from '@animo-editor/format';

  let { db }: { db: ProjectDB } = $props();

  interface Row {
    record: ProjectRecord;
    summary?: ProjectSummary;
    thumbnail?: string;
    /** For a folder project: whether the browser lets the page edit its folder without asking. */
    allowed?: boolean;
  }

  /** Whether this browser can edit a folder in place (Chrome and Edge). */
  const folders = canOpenFolders();

  let rows = $state<Row[]>([]);
  let loaded = $state(false);
  let used = $state<number | undefined>();
  let message = $state<{ kind: 'error' | 'info'; text: string } | undefined>();
  let busy = $state(false);
  let dragging = $state(false);

  let name = $state('Untitled game');
  let width = $state(320);
  let height = $state(240);
  let grid = $state(16);
  let start = $state<'empty' | 'example'>('empty');
  /** The kit a new empty project lists, or '' for none. */
  let kit = $state('');
  const kits = FIRST_PARTY_PLUGINS.filter((p) => p.kit);
  let saveIn = $state<'browser' | 'folder'>('browser');
  /** A picked folder with no project.json, waiting for the user to make it a project. */
  let unmade = $state<FileSystemDirectoryHandle | undefined>();

  let zipPicker: HTMLInputElement;
  let folderPicker: HTMLInputElement;
  let nameInput: HTMLInputElement;

  const editorUrl = (id: string) => `${import.meta.env.BASE_URL}editor.html?project=${encodeURIComponent(id)}`;

  function revokeThumbnails() {
    for (const row of rows) if (row.thumbnail) URL.revokeObjectURL(row.thumbnail);
  }

  async function refresh() {
    const records = await db.projects();
    const next: Row[] = [];
    for (const record of records) {
      if (record.folder) {
        next.push(await folderRow(record, record.folder));
        continue;
      }
      const files = await db.files(record.id);
      const summary = summarize(files);
      const image = summary.thumbnail && files.get(summary.thumbnail);
      next.push({ record, summary, thumbnail: image ? URL.createObjectURL(new Blob([image as BlobPart])) : undefined });
    }
    revokeThumbnails();
    rows = next;
    loaded = true;
    used = (await navigator.storage?.estimate?.())?.usage ?? next.reduce((total, row) => total + (row.record.folder ? 0 : (row.summary?.bytes ?? 0)), 0);
  }

  /** A folder project's row. Its files are counted by name and size, without reading them, and only when the browser allows. */
  async function folderRow(record: ProjectRecord, folder: FileSystemDirectoryHandle): Promise<Row> {
    if ((await folderAccess(folder).catch(() => 'denied')) !== 'granted') return { record, allowed: false };
    try {
      const sizes = new Map<string, Uint8Array>();
      const files = new Map<string, File>();
      for (const { path, handle } of await walk(folder)) {
        const file = await handle.getFile();
        files.set(path, file);
        // summarize only needs each file's size.
        sizes.set(path, { byteLength: file.size } as Uint8Array);
      }
      const summary = summarize(sizes);
      const image = summary.thumbnail && files.get(summary.thumbnail);
      return { record, summary, allowed: true, thumbnail: image ? URL.createObjectURL(image) : undefined };
    } catch {
      // The folder was moved or deleted.
      return { record, allowed: true };
    }
  }

  onMount(() => {
    void refresh().catch(fail);
  });
  onDestroy(revokeThumbnails);

  function fail(e: unknown) {
    message = { kind: 'error', text: e instanceof Error ? e.message : String(e) };
  }

  /** Runs one action at a time, showing its error if it fails. */
  async function run(action: () => Promise<void>) {
    if (busy) return;
    busy = true;
    message = undefined;
    try {
      await action();
    } catch (e) {
      fail(e);
    } finally {
      busy = false;
    }
  }

  /** Asks the browser not to clear the projects when space runs low. */
  function keep() {
    void navigator.storage?.persist?.().catch(() => {});
  }

  async function add(projectName: string, files: Map<string, Uint8Array>): Promise<ProjectRecord> {
    keep();
    return db.create(projectName, files);
  }

  function create(event: SubmitEvent) {
    event.preventDefault();
    void run(async () => {
      const projectName = name.trim() || 'Untitled game';
      let dir: FileSystemDirectoryHandle | undefined;
      if (folders && saveIn === 'folder') {
        dir = await pickFolder();
        if (!dir) return;
        if (!(await isEmptyFolder(dir))) throw new Error(`${dir.name} already has files in it. Choose an empty folder, or make a new one in the picker.`);
      }
      const files =
        start === 'example' ? withGuide(withName(await exampleProject(), projectName)) : emptyProject({ name: projectName, width, height, grid, ...(kit && { plugins: [kit] }) });
      let record: ProjectRecord;
      if (dir) {
        keep();
        await writeFiles(dir, files);
        record = await db.createFolder(projectName, dir);
      } else {
        record = await add(projectName, files);
      }
      location.href = editorUrl(record.id);
    });
  }

  async function importEntries(entries: Map<string, Uint8Array>) {
    const { files, name: projectName } = projectFiles(entries);
    await add(projectName, files);
    await refresh();
    message = { kind: 'info', text: `Imported ${projectName}` };
  }

  function importZips(list: FileList | null) {
    const files = [...(list ?? [])];
    zipPicker.value = '';
    void run(async () => {
      for (const file of files) await importEntries(filesFromZip(new Uint8Array(await file.arrayBuffer())));
    });
  }

  /** Opens the folder project for a folder: the one already on the list, a new one, or asks to make one. */
  async function openFolder(dir: FileSystemDirectoryHandle) {
    keep();
    const existing = await db.findFolder(dir);
    if (existing) {
      location.href = editorUrl(existing.id);
      return;
    }
    const top = new FolderStorage(dir);
    const text = await top.read('project.json');
    if (text === undefined) {
      for (const { path } of await walk(dir)) {
        if (path.split('/').length === 2 && path.endsWith('/project.json')) {
          throw new Error(`${dir.name} has project.json in its ${path.split('/')[0]} folder. Open that folder instead.`);
        }
      }
      unmade = dir;
      return;
    }
    let projectName = dir.name;
    try {
      const json = JSON.parse(text) as { name?: unknown };
      if (typeof json.name === 'string' && json.name.trim()) projectName = json.name.trim();
    } catch {
      throw new Error(`project.json in ${dir.name} is not valid JSON.`);
    }
    const record = await db.createFolder(projectName, dir);
    location.href = editorUrl(record.id);
  }

  function chooseFolder() {
    if (!folders) return folderPicker.click();
    void run(async () => {
      const dir = await pickFolder();
      if (dir) await openFolder(dir);
    });
  }

  /** Makes a folder with no project.json into a project in place: its files stay where they are. */
  function makeProject() {
    const dir = unmade;
    if (!dir) return;
    void run(async () => {
      const files = emptyProject({ name: dir.name, width, height, grid });
      const json = JSON.parse(new TextDecoder().decode(files.get('project.json'))) as Record<string, unknown>;
      const scene = files.get('public/scenes/main.scene.json')!;
      const add = new Map([['scenes/main.scene.json', scene]]);
      const storage = new FolderStorage(dir);
      for (const [name, text] of guideFiles('.')) if (!(await storage.exists(name))) add.set(name, new TextEncoder().encode(text));
      if (await storage.exists('scenes/main.scene.json')) add.delete('scenes/main.scene.json');
      await writeFiles(dir, add);
      await storage.create('project.json', new TextEncoder().encode(`${JSON.stringify({ ...json, root: '.' }, null, 2)}\n`));
      unmade = undefined;
      const record = await db.createFolder(dir.name, dir);
      location.href = editorUrl(record.id);
    });
  }

  function allowAndOpen(record: ProjectRecord) {
    void run(async () => {
      if (!(await requestFolderAccess(record.folder!))) throw new Error(`The editor needs your OK to edit ${record.folder!.name}. Click Allow and open, then Allow in the browser's prompt.`);
      location.href = editorUrl(record.id);
    });
  }

  function importFolder(list: FileList | null) {
    const files = [...(list ?? [])];
    folderPicker.value = '';
    if (files.length) void run(async () => importEntries(await filesFromFolder(files)));
  }

  /** Every file under a dropped folder, with its path from the folder's parent. */
  async function readEntry(entry: FileSystemEntry, out: Map<string, Uint8Array>): Promise<void> {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
      out.set(entry.fullPath.replace(/^\/+/, ''), new Uint8Array(await file.arrayBuffer()));
      return;
    }
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    // readEntries returns the folder in batches until it returns none.
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
      if (!batch.length) break;
      for (const child of batch) await readEntry(child, out);
    }
  }

  function drop(event: DragEvent) {
    event.preventDefault();
    dragging = false;
    const items = [...(event.dataTransfer?.items ?? [])].map((item) => item.webkitGetAsEntry?.()).filter((e): e is FileSystemEntry => !!e);
    const files = [...(event.dataTransfer?.files ?? [])];
    // In Chrome and Edge a dropped folder opens in place. The handles must be asked for before the event ends.
    type HandleItem = DataTransferItem & { getAsFileSystemHandle?(): Promise<FileSystemHandle | null> };
    const handles = folders ? [...(event.dataTransfer?.items ?? [])].map((item) => (item as HandleItem).getAsFileSystemHandle?.()) : [];
    void run(async () => {
      if (!items.length && !files.length) return;
      if (items.length === 1 && items[0]!.isDirectory && handles[0]) {
        const handle = await handles[0];
        if (handle?.kind === 'directory') return openFolder(handle as FileSystemDirectoryHandle);
      }
      for (const [i, entry] of items.entries()) {
        if (entry.isDirectory) {
          const entries = new Map<string, Uint8Array>();
          await readEntry(entry, entries);
          await importEntries(entries);
        } else if (files[i]?.name.toLowerCase().endsWith('.zip')) {
          await importEntries(filesFromZip(new Uint8Array(await files[i].arrayBuffer())));
        } else {
          throw new Error(`${entry.name} is not a project. Drop a .zip or a folder with project.json in it.`);
        }
      }
    });
  }

  function exportZip(record: ProjectRecord) {
    void run(async () => {
      if (record.folder && !(await requestFolderAccess(record.folder))) throw new Error(`The editor needs your OK to read ${record.folder.name}.`);
      downloadZip(record.name, await db.files(record.id));
      await db.update(record.id, { exported: Date.now() });
      await refresh();
    });
  }

  /** Duplicates a project. A folder project's copy goes into this browser. */
  function duplicate(record: ProjectRecord) {
    void run(async () => {
      if (record.folder && !(await requestFolderAccess(record.folder))) throw new Error(`The editor needs your OK to read ${record.folder.name}.`);
      const copy = `${record.name} copy`;
      await add(copy, withName(await db.files(record.id), copy));
      await refresh();
    });
  }

  /** False when the project is open in another tab, which holds its lock. */
  async function closed(id: string): Promise<boolean> {
    if (!navigator.locks) return true;
    const held = await navigator.locks.query();
    return !held.held?.some((lock) => lock.name === `animo-project:${id}`);
  }

  function remove(record: ProjectRecord) {
    void run(async () => {
      if (!(await closed(record.id))) throw new Error(`${record.name} is open in another tab. Close it there first.`);
      const question = record.folder
        ? `Remove ${record.name} from this list? Its folder ${record.folder.name} and its files stay where they are.`
        : `Delete ${record.name} from this browser? This can't be undone. Export a .zip first to keep a copy.`;
      if (!confirm(question)) return;
      await db.remove(record.id);
      await refresh();
    });
  }

  const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  function ago(time: number): string {
    const seconds = (time - Date.now()) / 1000;
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ['year', 31_536_000],
      ['month', 2_592_000],
      ['week', 604_800],
      ['day', 86_400],
      ['hour', 3600],
      ['minute', 60],
    ];
    for (const [unit, size] of units) if (Math.abs(seconds) >= size) return relative.format(Math.round(seconds / size), unit);
    return 'just now';
  }

  function megabytes(bytes: number): string {
    if (bytes < 102_400) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / 1_048_576).toFixed(1)} MB`;
  }

  const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

  function contents(s: ProjectSummary): string {
    const parts = [count(s.scenes, 'scene'), count(s.images, 'image')];
    if (s.tilemaps) parts.push(count(s.tilemaps, 'tilemap'));
    return parts.join(' · ');
  }

  function storedIn(list: Row[]): string {
    const inFolders = list.filter((row) => row.record.folder).length;
    if (!inFolders) return `Stored in this browser · ${count(list.length, 'project')}`;
    return `${count(inFolders, 'folder')} · ${list.length - inFolders} in this browser`;
  }

  function exportState(record: ProjectRecord): string {
    if (!record.exported) return 'never exported';
    if (record.updated > record.exported) return `changed since last export, ${ago(record.exported)}`;
    return `last exported ${ago(record.exported)}`;
  }
</script>

<header class="top">
  <span class="brand">animo-editor</span>
  <span class="muted">in your browser</span>
  <div class="actions">
    <button class="btn" disabled={busy} onclick={() => zipPicker.click()}>Import .zip</button>
    {#if folders}
      <button class="btn" disabled={busy} onclick={chooseFolder} title="Edit a project folder on your computer in place">Open a folder</button>
    {:else}
      <button class="btn" disabled={busy} onclick={chooseFolder} title="Copies the folder into this browser. Chrome and Edge can edit a folder in place.">Import a folder</button>
    {/if}
    <button class="btn primary" onclick={() => nameInput.focus()}>New project</button>
  </div>
  <input bind:this={zipPicker} type="file" accept=".zip,application/zip" multiple hidden onchange={() => importZips(zipPicker.files)} />
  <input bind:this={folderPicker} type="file" webkitdirectory hidden onchange={() => importFolder(folderPicker.files)} />
</header>

<div class="page">
  <main>
    <div class="title">
      <h1>Projects</h1>
      {#if loaded}
        <span class="muted small">{storedIn(rows)}{used !== undefined ? ` · ${megabytes(used)} used` : ''}</span>
      {/if}
    </div>
    <p class="note">
      {#if folders}Folder projects save straight to your files. Other projects live only in this browser: export a .zip to keep a copy, share it, or open it with <code>animo-editor dev</code> in a game repo.
      {:else}Projects live only in this browser. Export a .zip to keep a copy, share it, or open it with <code>animo-editor dev</code> in a game repo.
        <span class="hint">In Chrome or Edge you can open a project folder and edit its files in place, so saves go straight to disk and changes made by other tools, such as an AI assistant, show up live.</span>{/if}
    </p>
    {#if message}
      <p class={message.kind} role={message.kind === 'error' ? 'alert' : 'status'}>{message.text}</p>
    {/if}
    {#if unmade}
      <div class="ask" role="dialog" aria-label="Make it a project">
        <p><strong>{unmade.name}</strong> has no project.json yet.</p>
        <p class="muted small">Make it a project? This adds project.json and an empty scenes/main.scene.json to the folder. Nothing else changes.</p>
        <div class="row-actions">
          <button class="btn small primary" disabled={busy} onclick={makeProject}>Make it a project</button>
          <button class="btn small" onclick={() => (unmade = undefined)}>Cancel</button>
        </div>
      </div>
    {/if}
    {#if rows.length}
      <ul class="projects" aria-label="Projects">
        {#each rows as { record, summary, thumbnail, allowed } (record.id)}
          <li>
            <div class="thumb">{#if thumbnail}<img src={thumbnail} alt="" />{/if}</div>
            <div class="details">
              <span class="title-line">
                <a class="name" href={editorUrl(record.id)}>{record.name}</a>
                {#if record.folder}
                  <span class="badge" class:warn={!allowed}>{allowed ? `Folder · ${record.folder.name}` : 'Folder · needs your OK'}</span>
                {:else if folders}
                  <span class="badge browser">This browser</span>
                {/if}
              </span>
              <span class="muted small">{summary ? `${contents(summary)} · ` : ''}edited {ago(record.updated)}</span>
              {#if record.folder}
                <span class="muted mono">
                  {#if allowed}Saves to your files · <span class="live">picks up outside changes</span>{:else}Chrome asks again after a restart before the editor can use {record.folder.name}{/if}
                </span>
              {:else}
                <span class="muted mono">{megabytes(summary?.bytes ?? 0)} · {exportState(record)}</span>
              {/if}
            </div>
            <div class="row-actions">
              {#if record.folder && !allowed}
                <button class="btn small primary" disabled={busy} onclick={() => allowAndOpen(record)}>Allow and open</button>
              {:else}
                <a class="btn small primary" href={editorUrl(record.id)}>Open</a>
              {/if}
              <button class="btn small" disabled={busy} onclick={() => exportZip(record)}>Export .zip</button>
              {#if record.folder}
                <button class="btn small" disabled={busy} onclick={() => duplicate(record)}>Copy into browser</button>
                <button class="btn small danger" disabled={busy} aria-label={`Remove ${record.name}`} onclick={() => remove(record)}>Remove</button>
              {:else}
                <button class="btn small" disabled={busy} onclick={() => duplicate(record)}>Duplicate</button>
                <button class="btn small danger" disabled={busy} aria-label={`Delete ${record.name}`} onclick={() => remove(record)}>Delete</button>
              {/if}
            </div>
          </li>
        {/each}
      </ul>
    {:else if loaded}
      <p class="empty muted">No projects in this browser yet. Start one on the right, or import a .zip or folder.</p>
    {/if}
    <div
      class="drop"
      class:dragging
      role="region"
      aria-label="Drop a project to import it"
      ondragover={(e) => {
        e.preventDefault();
        dragging = true;
      }}
      ondragleave={() => (dragging = false)}
      ondrop={drop}
    >
      {folders ? 'Drop a folder here to open it, or a .zip to import it' : 'Drop a project .zip or folder here to import it'}
    </div>
  </main>

  <aside aria-label="New project">
    <form onsubmit={create}>
      <h2>New project</h2>
      <label>Name<input bind:this={nameInput} bind:value={name} required /></label>
      <div class="grid">
        <label>Width<input class="mono" type="number" min="1" bind:value={width} disabled={start === 'example'} /></label>
        <label>Height<input class="mono" type="number" min="1" bind:value={height} disabled={start === 'example'} /></label>
        <label>Grid<input class="mono" type="number" min="1" bind:value={grid} disabled={start === 'example'} /></label>
      </div>
      <fieldset>
        <legend>Start from</legend>
        <label class="choice"><input type="radio" bind:group={start} value="empty" /> An empty scene</label>
        <label class="choice"><input type="radio" bind:group={start} value="example" /> Mossy Ruins, a small platformer level (tilemap, physics, lights, particles, scripts)</label>
      </fieldset>
      {#if start === 'empty' && kits.length}
        <fieldset>
          <legend>Kind of game</legend>
          <label class="choice"><input type="radio" bind:group={kit} value="" /> Blank</label>
          {#each kits as k (k.name)}
            <label class="choice"><input type="radio" bind:group={kit} value={k.name} /> {k.game ?? k.label} <span class="muted small">{k.label}</span></label>
          {/each}
          <span class="muted small">Plugins can be added later from Plugins in the editor.</span>
        </fieldset>
      {/if}
      {#if folders}
        <fieldset>
          <legend>Save in</legend>
          <label class="choice"><input type="radio" bind:group={saveIn} value="browser" /> This browser</label>
          <label class="choice"><input type="radio" bind:group={saveIn} value="folder" /> A folder on your computer</label>
        </fieldset>
      {/if}
      <button class="btn primary" type="submit" disabled={busy}>{folders && saveIn === 'folder' ? 'Choose folder and create' : 'Create and open'}</button>
      <p class="muted small">
        {#if folders && saveIn === 'folder'}
          A folder project edits scenes, art, animations, tilemaps and prefabs, attaches the game's scripts, and plays the game with its code.
        {:else}
          The browser version edits scenes, art, animations, tilemaps and prefabs, and plays the game with its code. Plugins from npm need <code>animo-editor dev</code>.
        {/if}
      </p>
    </form>
  </aside>
</div>

<style>
  :global(:root) {
    --bg: #14161b;
    --panel: #1b1e24;
    --border: #2c313a;
    --text: #e6e8ec;
    --text-2: #c9cfd9;
    --muted: #a3abb9;
    --accent: #4da3ff;
    --accent-bg: #1d3550;
    --accent-ink: #0b1520;
    --danger: #ef8a80;
    --dashed: #4a5262;
    --sans: 'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', sans-serif;
    --mono: 'IBM Plex Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace;
    color-scheme: dark;
  }
  :global(*) {
    box-sizing: border-box;
  }
  :global(body) {
    margin: 0;
    min-height: 100vh;
    background: var(--bg);
    color: var(--text);
    font-family: var(--sans);
    font-size: 13px;
  }
  :global(input, button) {
    font-family: inherit;
    font-size: inherit;
    color: inherit;
  }
  .muted {
    color: var(--muted);
  }
  .small {
    font-size: 12px;
  }
  .mono,
  code {
    font-family: var(--mono);
  }
  .mono {
    font-size: 11px;
  }
  .top {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    padding: 8px 16px;
    border-bottom: 1px solid var(--border);
    background: var(--panel);
  }
  .top .muted {
    font-size: 12px;
  }
  .brand {
    font-weight: 600;
    font-size: 15px;
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-left: auto;
  }
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 36px;
    padding: 0 14px;
    border-radius: 6px;
    border: 1px solid var(--border);
    background: transparent;
    color: var(--text-2);
    text-decoration: none;
    cursor: pointer;
  }
  .btn:hover {
    border-color: var(--dashed);
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: default;
  }
  .btn.primary {
    border: 0;
    background: var(--accent);
    color: var(--accent-ink);
    font-weight: 600;
  }
  .btn.small {
    min-height: 32px;
    padding: 0 10px;
    font-size: 12px;
  }
  .btn.danger {
    color: var(--danger);
  }
  .btn:focus-visible,
  .name:focus-visible,
  input:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  .page {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    padding: 24px;
    max-width: 1280px;
    margin: 0 auto;
  }
  main {
    flex: 3 1 560px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .title {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 12px;
  }
  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }
  h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }
  p {
    margin: 0;
    line-height: 1.5;
  }
  .note {
    padding: 10px 12px;
    border-radius: 6px;
    background: #2a2416;
    border: 1px solid #574d33;
    color: #f2c27f;
    font-size: 12px;
  }
  .error,
  .info {
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
  }
  .error {
    background: #3a1d1b;
    border: 1px solid #6b3530;
    color: var(--danger);
  }
  .info {
    background: var(--accent-bg);
    border: 1px solid #2f5582;
    color: #9ccbff;
  }
  .projects {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--panel);
    overflow: hidden;
  }
  .projects li {
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr) auto;
    gap: 16px;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid var(--border);
  }
  .projects li:last-child {
    border-bottom: 0;
  }
  .projects li:hover,
  .projects li:focus-within {
    background: var(--accent-bg);
    box-shadow: inset 2px 0 0 var(--accent);
  }
  .thumb {
    width: 56px;
    height: 42px;
    border-radius: 6px;
    background: #2a3a52;
    border: 1px solid #3a4c66;
    overflow: hidden;
  }
  .thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    image-rendering: pixelated;
  }
  .details {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .name {
    color: var(--text);
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .title-line {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  .badge {
    flex: none;
    font: 500 11px var(--mono);
    padding: 1px 7px;
    border-radius: 10px;
    background: #173327;
    color: #7fd4a8;
    border: 1px solid #2c5a44;
  }
  .badge.warn {
    background: #2a2416;
    color: #f2c27f;
    border-color: #574d33;
  }
  .badge.browser {
    background: transparent;
    color: var(--muted);
    border-color: var(--border);
  }
  .hint {
    display: block;
    margin-top: 4px;
    color: var(--text-2);
  }
  .live {
    color: #7fd4a8;
  }
  .ask {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 14px;
    border: 1px solid var(--border);
    border-radius: 6px;
    background: var(--panel);
  }
  .ask p {
    margin: 0;
  }
  .name:hover {
    text-decoration: underline;
  }
  .row-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: end;
  }
  .empty {
    padding: 24px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--panel);
    text-align: center;
  }
  .drop {
    border: 2px dashed var(--dashed);
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    color: var(--muted);
    font-size: 12px;
  }
  .drop.dragging {
    border-color: var(--accent);
    background: var(--accent-bg);
    color: var(--text);
  }
  aside {
    flex: 1 1 300px;
    min-width: 0;
    align-self: start;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--panel);
    padding: 16px;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    color: var(--muted);
  }
  input:not([type='radio']) {
    min-width: 0;
    min-height: 32px;
    padding: 0 8px;
    border-radius: 6px;
    border: 1px solid var(--border);
    background: var(--bg);
    color: var(--text);
  }
  input:disabled {
    opacity: 0.5;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
  fieldset {
    border: 0;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  legend {
    font-size: 12px;
    color: var(--muted);
    margin-bottom: 6px;
  }
  .choice {
    flex-direction: row;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text);
  }
  @media (max-width: 640px) {
    .page {
      padding: 16px;
    }
    .projects li {
      grid-template-columns: 56px minmax(0, 1fr);
    }
    .row-actions {
      grid-column: 1 / -1;
      justify-content: start;
    }
  }
</style>
